// src/services/properties.js

import { apiUrl } from '../config/api';
import { getLocalZoneDemo } from '../data/localZoneDemoProperties';

const propertyDetailCache = new Map();
const propertyDetailRequests = new Map();
const PROPERTY_DETAIL_CACHE_MS = 90 * 1000;

function cachedProperty(id) {
  const entry = propertyDetailCache.get(String(id));
  if (!entry) return null;
  if (Date.now() - entry.savedAt > PROPERTY_DETAIL_CACHE_MS) {
    propertyDetailCache.delete(String(id));
    return null;
  }
  return entry.data;
}

async function requestPropertyById(id) {
  const key = String(id);
  const localDemo = getLocalZoneDemo(key);
  if (localDemo) return localDemo;
  if (propertyDetailRequests.has(key)) return propertyDetailRequests.get(key);
  const request = fetch(apiUrl(`/properties/${encodeURIComponent(key)}`))
    .then(async (res) => {
      if (!res.ok) throw new Error('Propiedad no encontrada');
      const data = await res.json();
      propertyDetailCache.set(key, { data, savedAt: Date.now() });
      return data;
    })
    .finally(() => propertyDetailRequests.delete(key));
  propertyDetailRequests.set(key, request);
  return request;
}

/**
 * Obtener propiedades (con filtros opcionales)
 */
export async function getProperties(query = '') {
  const url = query
    ? apiUrl(`/properties?${query}`)
    : apiUrl('/properties');

  const res = await fetch(url);

  if (!res.ok) {
    throw new Error('Error al obtener propiedades');
  }

  return res.json();
}

/**
 * Obtener una propiedad por ID
 */
export async function getPropertyById(id, { preferCache = false } = {}) {
  const cached = preferCache ? cachedProperty(id) : null;
  return cached || requestPropertyById(id);
}

export function prefetchPropertyById(id) {
  if (!id || cachedProperty(id)) return Promise.resolve(cachedProperty(id));
  return requestPropertyById(id).catch(() => null);
}

/**
 * Crear una nueva propiedad (requiere auth)
 */
export async function createProperty(propertyData) {
  const res = await fetch(apiUrl('/properties'), {
    method: 'POST',
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(propertyData),
  });

  let data = null;

  try {
    data = await res.json();
  } catch {
    // Algunos proxies devuelven una respuesta vacía ante errores de infraestructura.
  }

  if (!res.ok) {
    if (res.status === 401) {
      throw new Error('Tu sesión venció. Volvé a ingresar para publicar la propiedad.');
    }

    const error = new Error(data?.message || `No pudimos publicar la propiedad (error ${res.status}).`);
    error.status = res.status;
    error.code = data?.code;
    error.details = data;
    throw error;
  }

  return data;
}

export async function getPublicationCapacity() {
  const res = await fetch(apiUrl('/properties/publication-capacity'), {
    credentials: 'include',
  });
  const data = await res.json().catch(() => null);
  if (!res.ok) {
    const error = new Error(res.status === 401 ? 'Tu sesión venció. Volvé a ingresar para continuar.' : data?.message || 'No pudimos consultar tu plan.');
    error.status = res.status;
    error.code = data?.code;
    throw error;
  }
  return data;
}

/**
 * Actualizar una propiedad (requiere auth)
 */
export async function updateProperty(id, propertyData) {
  const res = await fetch(apiUrl(`/properties/${id}`), {
    method: 'PUT',
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(propertyData),
  });

  const data = await res.json();

  if (!res.ok) {
    throw new Error(data.message || 'Error al actualizar propiedad');
  }

  return data;
}

export async function deleteProperty(id) {
  const res = await fetch(apiUrl(`/properties/${id}`), {
    method: 'DELETE',
    credentials: 'include',
  });
  const data = await res.json().catch(() => null);
  if (!res.ok) throw new Error(data?.message || 'No pudimos eliminar la publicación.');
  return data;
}

/**
 * Obtener departamentos SEO dinámicos
 */
export async function getSeoDepartments() {
  const res = await fetch(apiUrl('/properties/seo-departments'));

  if (!res.ok) {
    throw new Error("Error obteniendo departamentos SEO");
  }

  return res.json();
}
